import React, { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "src/contexts/NotificationContext";
import { markNotificationAsRead, fetchInAppNotificationsFull } from "src/services/NotificationAnnouncementsAPIs";
import { handleSubMenuClick } from "src/layouts/HomePage/SidebarManagement/HomepageLayout";
import DateRangePicker from "../../../components/reusable/DateRangePicker/DateRangePicker";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import TablePagePagination from "src/components/common/TablePagePagination/TablePagePagination";
import NotificationCategoryIcon from "src/components/reusable/NotificationCategoryIcon";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/useLanguage";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  InAppNotification,
  getDashboardNameFromCategory,
} from "src/layouts/MyAccount/Notifications/NotificationInterfaces";
import { toPktDateString } from "src/layouts/MyAccount/Notifications/NotificationFilters";
import NotificationStateDisplay from "src/components/reusable/NotificationStateDisplay";

const PAGE_SIZE = 10;

type PresetFilter = "today" | "thisWeek" | "lastWeek";
type Filter = PresetFilter | "dateRange";

const FILTER_TO_DATE_RANGE: Record<PresetFilter, "today" | "this_week" | "last_week"> = {
  today: "today",
  thisWeek: "this_week",
  lastWeek: "last_week",
};

interface AllNotificationsLocationState {
  targetNotificationId?: number;
  createdAt?: string;
}

const Notifications: React.FC = () => {
  const { refreshCount, syncUnreadWithFetchedList } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<Filter>("today");
  const { t, i18n } = useTranslation();
  useLanguage();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: "",
    endDate: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [highlightedId, setHighlightedId] = useState<number | null>(null);

  // Deep-link support: the bell popup navigates here with a target notification
  // id (+ its created_at) in router state. These refs make sure that jump only
  // ever happens once per arrival — after that, normal filter/page interactions
  // behave as usual.
  const targetNotificationId = (location.state as AllNotificationsLocationState | null)
    ?.targetNotificationId;
  const targetHandledRef = useRef(false);
  const skipNextPageResetRef = useRef(false);
  // Guards against a stale response (e.g. a slow earlier fetch) overwriting a
  // newer one — every fetch captures the sequence number current at its start
  // and only applies its result if nothing newer has started since.
  const requestSeqRef = useRef(0);

  // Main list fetch — server-side paginated and date-filtered. Fires whenever
  // the filter, custom date range, or page changes.
  useEffect(() => {
    // Arriving via a bell-popup deep link: skip this default-state ("today",
    // page 1) fetch entirely rather than firing it and immediately discarding
    // the result — the deep-link effect below resolves the real filter/date/
    // page and this effect re-runs with that once it does. Without this guard,
    // both effects hit /notifications/in-app at the same time on first mount.
    if (targetNotificationId && !targetHandledRef.current) {
      return;
    }

    const seq = ++requestSeqRef.current;
    setIsLoading(true);

    const params: Parameters<typeof fetchInAppNotificationsFull>[0] = {
      page: currentPage,
      page_size: PAGE_SIZE,
      lang: getApiLangFromI18n(i18n),
    };
    if (selectedFilter === "dateRange") {
      if (dateRange.startDate) {
        params.start_date = dateRange.startDate;
      }
      if (dateRange.endDate) {
        params.end_date = dateRange.endDate;
      }
    } else {
      params.date_range = FILTER_TO_DATE_RANGE[selectedFilter];
    }

    (async () => {
      try {
        const data = await fetchInAppNotificationsFull(params);
        if (requestSeqRef.current !== seq) {
          return;
        }
        setNotifications(data.notifications);
        setTotalPages(data.pagination.totalPages);
        setError(null);
        // `unread_count` is the account-wide total, not just this page, so the
        // bell badge doesn't undercount based on whatever page happens to be showing.
        syncUnreadWithFetchedList(data.unread_count);
      } catch (err) {
        if (requestSeqRef.current !== seq) {
          return;
        }
        console.error("Error fetching notifications:", err);
        setNotifications([]);
        setError(
          t("allNotifications.errors.loadFailed", {
            defaultValue:
              "Something went wrong while loading this page. Please try again, or refresh the page.",
          })
        );
      } finally {
        if (requestSeqRef.current === seq) {
          setIsLoading(false);
        }
      }
    })();
  }, [
    selectedFilter,
    dateRange.startDate,
    dateRange.endDate,
    currentPage,
    i18n,
    syncUnreadWithFetchedList,
    targetNotificationId,
    t,
  ]);

  // Jump straight to whichever page contains the notification the user clicked
  // in the bell popup. There's no full client-side list to search anymore, so
  // this resolves it via one unpaginated lookup scoped to that notification's
  // own (PKT) calendar day — bounded to a single day's volume — then sets the
  // main fetch's filter/page state to load and display that exact page.
  useEffect(() => {
    if (targetHandledRef.current || !targetNotificationId) {
      return;
    }
    targetHandledRef.current = true;

    const state = location.state as AllNotificationsLocationState | null;
    const createdAt = state?.createdAt;
    if (!createdAt) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    const day = toPktDateString(createdAt);

    (async () => {
      try {
        const lookup = await fetchInAppNotificationsFull({
          start_date: day,
          end_date: day,
          lang: getApiLangFromI18n(i18n),
        });
        const index = lookup.notifications.findIndex(
          n => n.notification_id === targetNotificationId
        );
        if (index === -1) {
          // Not in that day's results (e.g. deleted since the popup fetched it) —
          // leave the page on its default state rather than jumping nowhere.
          return;
        }

        const page = Math.floor(index / PAGE_SIZE) + 1;
        skipNextPageResetRef.current = true;
        setDateRange({ startDate: day, endDate: day });
        setSelectedFilter("dateRange");
        setCurrentPage(page);
        setHighlightedId(targetNotificationId);
      } catch (error) {
        console.error("Error resolving deep-linked notification:", error);
      } finally {
        // Strip the router state so a later refresh/back-nav doesn't repeat the jump.
        navigate(location.pathname, { replace: true, state: {} });
      }
    })();
  }, [targetNotificationId, location, navigate, i18n]);

  // Reset back to page 1 whenever the filter or date range changes, so a
  // filter/date change never leaves the view stranded on a now-empty page.
  // Skipped once right after the deep-link effect above deliberately sets a
  // specific page, so that jump isn't immediately undone.
  useEffect(() => {
    if (skipNextPageResetRef.current) {
      skipNextPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [selectedFilter, dateRange.startDate, dateRange.endDate]);

  // Scrolls the deep-linked row into view once its page has actually loaded,
  // then clears the highlight after a few seconds. Depends on `notifications`
  // (not just `highlightedId`/`currentPage`) because the row the deep-link
  // effect targets only exists in the DOM once the main fetch above resolves —
  // which happens asynchronously, after the state change that set highlightedId.
  useEffect(() => {
    if (!highlightedId) {return;}
    const raf = requestAnimationFrame(() => {
      document
        .querySelector(`[data-notification-id="${highlightedId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const clearTimer = setTimeout(() => setHighlightedId(null), 3000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(clearTimer);
    };
  }, [highlightedId, notifications]);

  const filterOptions = [
    { value: "today", label: t("allNotifications.filters.today") },
    { value: "thisWeek", label: t("allNotifications.filters.thisWeek") },
    { value: "lastWeek", label: t("allNotifications.filters.lastWeek") },
    {
      value: "dateRange",
      label: t("allNotifications.filters.custom", { defaultValue: "Custom Range" }),
      disabled: true,
    },
  ];

  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter as Filter);
  };

  const handleNotificationClick = async (notification: InAppNotification) => {
    try {
      const success = await markNotificationAsRead(notification.notification_id);
      if (!success) {
        throw new Error(t("allNotifications.errors.markReadFailed"));
      }

      setNotifications(prev =>
        prev.map(n =>
          n.notification_id === notification.notification_id
            ? { ...n, is_read: true }
            : n
        )
      );

      await refreshCount();

      const dashboardName = getDashboardNameFromCategory(notification.category);
      handleSubMenuClick(dashboardName, navigate);
    } catch (error) {
      console.error("Error opening dashboard from notification:", error);
      message.error(t("allNotifications.errors.openDashboardFailed"));
    }
  };

  const renderListBody = () => {
    if (isLoading) {
      return null;
    }
    if (error) {
      return (
        <NotificationStateDisplay
          variant="error"
          title={t("allNotifications.errors.loadFailedTitle", {
            defaultValue: "Something went wrong!",
          })}
          message={error}
        />
      );
    }
    if (notifications.length === 0) {
      return (
        <NotificationStateDisplay
          variant="empty"
          title={t("allNotifications.emptyTitle", { defaultValue: "No Notifications" })}
          message={t("allNotifications.noNotifications")}
        />
      );
    }

    return notifications.map(notification => {
      const created = new Date(notification.created_at);
      const isHighlighted = notification.notification_id === highlightedId;

      return (
        <button
          type="button"
          key={notification.notification_id}
          data-notification-id={notification.notification_id}
          className={`all-notification-row ${isHighlighted ? "all-notification-row--highlighted" : ""}`}
          onClick={() => handleNotificationClick(notification)}
        >
          <NotificationCategoryIcon category={notification.category} />
          <span className="all-notification-row__body">
            <span className="all-notification-row__title">{notification.category}</span>
            <span className="all-notification-row__description">
              {notification.content || notification.notification_content}
            </span>
            <span className="all-notification-row__meta">
              {!notification.is_read && (
                <span
                  className="all-notification-row__unread-dot"
                  aria-label={t("allNotifications.unread", { defaultValue: "Unread" })}
                />
              )}
              {`${String(created.getDate()).padStart(2, "0")}-${String(created.getMonth() + 1).padStart(2, "0")}-${created.getFullYear()}`}
              {" | "}
              {created.toLocaleTimeString(
                [i18n.language === "ar" ? "ar" : "en-US"],
                { hour: "2-digit", minute: "2-digit", hour12: true }
              )}
            </span>
          </span>
        </button>
      );
    });
  };

  return (
    <div className="notifications-container all-notifications-page">
      <h1 className="notifications-page-title">
        {t("allNotifications.title", { defaultValue: "All Notifications" })}
      </h1>
      <span className="notifications-page-description">
        {t("allNotifications.subtitle", {
          defaultValue:
            "Stay updated on sales, marketing, social media, and website performance insights.",
        })}
      </span>

      <div className="all-notifications-toolbar">
        <StyledDropdown
          className="filter-dropdown all-notifications-filter-select"
          popupClassName="filter-dropdown-menu"
          value={selectedFilter}
          onChange={handleFilterChange}
          options={filterOptions}
        />

        <div className="notification-date-range-wrapper">
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onRangeChange={({ start, end }) => {
              setDateRange({ startDate: start || "", endDate: end || "" });
              setSelectedFilter("dateRange");
            }}
          />
        </div>
      </div>

      <div className="notifications-list all-notifications-list">
        {renderListBody()}
      </div>

      {!isLoading && notifications.length > 0 && (
        <TablePagePagination
          className="all-notifications-pagination"
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={() => setCurrentPage(page => Math.max(1, page - 1))}
          onNext={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
        />
      )}
    </div>
  );
};

export default Notifications;
